import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { MapPin, Phone, Mail, Clock, Check } from "lucide-react";

const LOCATIONS = [
  {
    city: "Rīga",
    country: "Latvia",
    address: "Krasta iela 105, Rīga, LV-1019",
    phone: "+371 67 000 000",
    email: "riga@leduro.eu",
    hours: "Mon–Fri 08:00–17:00",
    role: "HQ · Main warehouse · Photometry lab",
  },
  {
    city: "Tallinn",
    country: "Estonia",
    address: "Peterburi tee 92, Tallinn, 11415",
    phone: "+372 600 0000",
    email: "tallinn@leduro.eu",
    hours: "Mon–Fri 08:30–17:30",
    role: "Regional warehouse · Trade pickup",
  },
  {
    city: "Vilnius",
    country: "Lithuania",
    address: "Verkių g. 29, Vilnius, 09108",
    phone: "+370 5 200 0000",
    email: "vilnius@leduro.eu",
    hours: "Mon–Fri 08:00–17:00",
    role: "Sales office · Local distributor",
  },
  {
    city: "Warsaw",
    country: "Poland",
    address: "Ul. Połczyńska 31A, Warsaw, 01-377",
    phone: "+48 22 000 00 00",
    email: "warsaw@leduro.eu",
    hours: "Mon–Fri 09:00–17:00",
    role: "Distributor partner",
  },
];

export default function WhereToFind() {
  const [sent, setSent] = useState(false);
  return (
    <div data-testid="where-to-find-page">
      <PageHero
        eyebrow="Where to Find"
        title="Offices, warehouses and trade partners across the EU."
        subtitle="Visit us, pick up trade orders, or schedule a sample installation in our Rīga lab."
        crumbs={[{ label: "Where to Find" }]}
      />

      {/* Map placeholder */}
      <section className="bg-leduro-surface py-12">
        <div className="leduro-container">
          <div className="relative aspect-[21/9] bg-leduro-navy overflow-hidden border border-leduro-border noise-overlay">
            <img
              src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=2000&q=80"
              alt="Map"
              className="absolute inset-0 h-full w-full object-cover opacity-40 grayscale"
            />
            <div className="absolute inset-0 grid-lines opacity-50" />

            {/* Pins */}
            {[
              { top: "44%", left: "55%", label: "Rīga" },
              { top: "38%", left: "58%", label: "Tallinn" },
              { top: "50%", left: "54%", label: "Vilnius" },
              { top: "54%", left: "48%", label: "Warsaw" },
            ].map((p, i) => (
              <div
                key={i}
                style={{ top: p.top, left: p.left }}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center gap-2"
                data-testid={`map-pin-${p.label}`}
              >
                <span className="relative h-3 w-3">
                  <span className="absolute inset-0 bg-leduro-orange rounded-full animate-pulse-glow" />
                  <span className="absolute inset-[3px] bg-white rounded-full" />
                </span>
                <span className="px-2 py-0.5 bg-leduro-orange text-white text-[10px] font-bold tracking-wider uppercase">
                  {p.label}
                </span>
              </div>
            ))}

            <div className="absolute bottom-6 left-6 right-6 md:left-10 md:right-auto md:max-w-md bg-white p-5 border border-leduro-border">
              <p className="leduro-eyebrow">— Coverage</p>
              <p className="mt-2 font-heading font-bold text-leduro-navy text-lg">
                4 hubs · 9 countries · 612 trade buyers
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="leduro-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-leduro-border border border-leduro-border">
            {LOCATIONS.map((l) => (
              <div
                key={l.city}
                data-testid={`location-${l.city.toLowerCase()}`}
                className="bg-white p-6 lg:p-7 hover:bg-leduro-surface transition-colors"
              >
                <span className="leduro-eyebrow">— {l.country}</span>
                <h3 className="mt-2 font-heading font-black text-leduro-navy text-2xl">{l.city}</h3>
                <p className="mt-1 text-xs uppercase tracking-wider font-bold text-leduro-orange">{l.role}</p>
                <ul className="mt-5 space-y-3 text-sm">
                  <li className="flex items-start gap-2 text-leduro-text"><MapPin className="h-4 w-4 text-leduro-orange shrink-0 mt-0.5" />{l.address}</li>
                  <li className="flex items-start gap-2 text-leduro-text"><Phone className="h-4 w-4 text-leduro-orange shrink-0 mt-0.5" />{l.phone}</li>
                  <li className="flex items-start gap-2 text-leduro-text"><Mail className="h-4 w-4 text-leduro-orange shrink-0 mt-0.5" />{l.email}</li>
                  <li className="flex items-start gap-2 text-leduro-text"><Clock className="h-4 w-4 text-leduro-orange shrink-0 mt-0.5" />{l.hours}</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="bg-leduro-surface py-20" data-testid="contact-section">
        <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="leduro-eyebrow">— Trade contact</span>
            <h2 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-4xl text-balance">
              Open a trade account or request a project quote.
            </h2>
            <p className="mt-5 text-leduro-muted leading-relaxed">
              Replies arrive within one business day from a real trade engineer — never an automated mailbox.
            </p>
            <div className="mt-8 space-y-3 text-leduro-navy">
              <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-leduro-orange" /> +371 67 000 000</div>
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-leduro-orange" /> sales@leduro.eu</div>
              <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-leduro-orange" /> Mon–Fri 08:00–17:00 EET</div>
            </div>
          </div>

          <form
            className="lg:col-span-7 bg-white border border-leduro-border p-8 space-y-5"
            onSubmit={(e) => { e.preventDefault(); setSent(true); }}
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" testid="contact-name" required />
              <Field label="Company" testid="contact-company" required />
              <Field label="Email" type="email" testid="contact-email" required />
              <Field label="Phone" testid="contact-phone" />
            </div>
            <Field label="Project / interest" testid="contact-subject" />
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-leduro-navy">Message</label>
              <textarea
                required
                rows={5}
                data-testid="contact-message"
                className="mt-1.5 w-full border border-leduro-border px-3 py-2.5 text-sm focus:outline-none focus:border-leduro-orange"
                placeholder="Tell us about your project, quantities and timeline…"
              />
            </div>
            {sent ? (
              <div data-testid="contact-success" className="bg-leduro-orange/10 border border-leduro-orange text-leduro-orange p-4 text-sm font-semibold flex items-center gap-2">
                <Check className="h-4 w-4" /> Thanks — your message is in. A trade engineer will reply within 24h.
              </div>
            ) : (
              <button type="submit" data-testid="contact-submit" className="leduro-btn-primary">
                Send message
              </button>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, type = "text", testid, required }) => (
  <div>
    <label className="text-xs uppercase tracking-wider font-bold text-leduro-navy">
      {label} {required && <span className="text-leduro-orange">*</span>}
    </label>
    <input
      type={type}
      required={required}
      data-testid={testid}
      className="mt-1.5 w-full border border-leduro-border px-3 py-2.5 text-sm focus:outline-none focus:border-leduro-orange"
    />
  </div>
);
